import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";
import { faqs, howItWorks, purchaseSeatsLanguages } from "./helpers";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import FindGamesBanner from "public/images/swbrailriders/Attend Banners (6).png";

const config: TeamConfig = {
  teamSchema: "swbrailriders_flex",
  browserTitle: "Rise of Legends Flex Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "SWB RailRiders",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-30379-baseball-sec",
    mapId: "blockmap2d",
    isShowAvailableSeatsButtonHidden: false,
  },

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats - you've unlocked 10% off your tickets! Add 1 more game for 20% off, 2 more games for 30% off or 4+ games for an exclusive batting practice opportunity.",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        discount: 20,
        customProgressBarMessage:
          "Congrats - you've unlocked 20% off your tickets! Add 1 more game for 30% off, or 3+ games for an exclusive batting practice opportunity.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 2,
        discount: 30,
        customProgressBarMessage:
          "Congrats - you've unlocked 30% off your tickets! Add 2+ games for an exclusive batting practice opportunity.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 2,
        discount: 30,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Congrats - you've unlocked 30% off your tickets! Add 1 more game for an exclusive batting practice opportunity.",
      },
      {
        id: 5,
        minSelectedAmount: 6,
        updatedAccountGroup: 2,
        discount: 30,
        customProgressBarMessage:
          "Congrats - you've unlocked an exclusive batting practice opportunity with the team on top of your 30% off your tickets! Add more games or checkout now.",
        customAditionalMessage:
          " for an exclusive batting practice opportunity?",
      },
    ],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL", // As specified

  // purchase seat configs
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 20,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
    isDisabledEventSelection: false,
    hideAllUpcomingGames: false,
    isRepProduct: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  isSaveCardAllowed: true,
  isBrainTreePaymentEnabled: false,
  shouldUseInternalLogin: false,
  oktaClientId: "",
  oktaIssuer: "",
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // URLs
  accountManagerUrl: "https://am.ticketmaster.com/swbyankees/",

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: FindGamesBanner.src,
    checkoutStatusPageImage: FindGamesBanner.src,
    logoImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/SWB_RailRiders_logo.png" +
      "?width=128&height=75",
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/SWB_RailRiders_logo.png",
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    progressBar: {
      staticProgressBarText: false,
    },
    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:5709692255"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (570) 969-2255
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#003366",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "", // Can be a React node as well
    checkoutTermsAndCondition: <DefaultCheckoutTerms />, // Using standard SeasonShare language
    registrationTermsAndCondition: <DefaultRegistrationTerms />, // Using standard SeasonShare language
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessDescription:
      "Your SWB RailRiders tickets are confirmed! You will be notified when your tickets are available to be managed.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
